import * as angular from "angular";
import {IController} from "angular";
import * as _ from "lodash";
import {ServiceAreasServiceType} from "../../../service-areas/angular/types/service-areas-service.type";
import {PlanningClusteringAreasRequestsService} from "../../services/planning-clustering-areas-requests.service";
import {ClusteringAreaModel} from "../../models/clustering-area.model";
import {PlanningAlertsService} from "../../services/planning-alerts.service";
import {ClusteringOwnerType} from "../../types/clustering-owner.type";
import {PlanningDialogService} from "../../services/planning-dialogs-service.service";

export class PlanningClusteringAreasSettingsCtrl implements IController {
  ownerId: string;
  templateId: string;
  projectId: string;
  draftId: string;
  clusteringAreaIds: string[];
  serviceAreaIds: string[];
  isLocalOnly: boolean;
  isDisabled: boolean;
  onChange: Function;
  onAreasUpdated: Function;

  public clusteringAreas: ClusteringAreaModel[] = [];
  public selectedServiceAreaIds: string[] = [];
  public serviceAreaTypes: ServiceAreasServiceType[] = [ServiceAreasServiceType.Planning];
  public locationAddress: any;
  public isLoading: boolean;
  public serviceAreaColorMap: any = {};
  private ownerType: ClusteringOwnerType;

  /* @ngInject */
  constructor(private planningClusteringAreasRequestsService: PlanningClusteringAreasRequestsService,
              private $serviceArea,
              private planningAlertsService: PlanningAlertsService,
              private planningDialogService: PlanningDialogService,
              private $log,
              private $q) {
  }

  $onInit() {
    this._resolveOwnerType();

    if (this.isLocalOnly) {
      this._loadLocalServiceAreas(this.serviceAreaIds || []);
    } else {
      this._loadClusteringAreas();
    }
  }

  $onChanges(changes) {
    if (changes.templateId || changes.projectId || changes.draftId || changes.ownerId) {
      this._resolveOwnerType();
    }

    if (this.isLocalOnly) {
      if (changes.serviceAreaIds && !changes.serviceAreaIds.isFirstChange()) {
        this._loadLocalServiceAreas(this.serviceAreaIds || []);
      }
      return;
    }

    const ownerChanged = (changes.ownerId && !changes.ownerId.isFirstChange()) ||
      (changes.templateId && !changes.templateId.isFirstChange()) ||
      (changes.projectId && !changes.projectId.isFirstChange()) ||
      (changes.draftId && !changes.draftId.isFirstChange());

    const idsChanged = changes.clusteringAreaIds && !changes.clusteringAreaIds.isFirstChange();

    // Skip reload when the ids already match the locally displayed areas
    // (e.g. right after we added/removed and emitted the change ourselves).
    if (ownerChanged || (idsChanged && !this._hasSameAreas(this.clusteringAreaIds))) {
      this._loadClusteringAreas();
    }
  }

  onServiceAreasChange({ids}) {
    if (!!this.isDisabled) {
      return;
    }

    const serviceAreaIds = ids || [];

    if (this.isLocalOnly) {
      this.selectedServiceAreaIds = serviceAreaIds;
      this._loadLocalServiceAreas(serviceAreaIds);
      this._emitLocalChange(serviceAreaIds);
      return;
    }

    if (!this._canManageAreas()) {
      return;
    }
    const addedServiceAreaIds = _.difference(serviceAreaIds, this.selectedServiceAreaIds);
    const removedServiceAreaIds = _.difference(this.selectedServiceAreaIds, serviceAreaIds);

    if (!addedServiceAreaIds.length && !removedServiceAreaIds.length) {
      return;
    }

    this.isLoading = true;
    let chain = this.$q.when();
    let updatedAreas = _.clone(this.clusteringAreas) || [];

    if (addedServiceAreaIds.length) {
      chain = chain.then(() => {
        return this._addClusteringAreas(addedServiceAreaIds)
          .then((response) => {
            const addedAreas = this._normalizeAreasResponse(response);
            updatedAreas = this._mergeAreas(updatedAreas, addedAreas);
          });
      });
    }

    _.forEach(removedServiceAreaIds, (serviceAreaId) => {
      const clusteringArea = _.find(this.clusteringAreas, (area) => {
        return this._getClusteringAreaServiceAreaId(area) === serviceAreaId;
      });
      if (clusteringArea?.id) {
        chain = chain.then(() => {
          return this._removeClusteringArea(clusteringArea.id)
            .then(() => {
              updatedAreas = _.reject(updatedAreas, {id: clusteringArea.id});
            });
        });
      }
    });

    chain
      .then(() => {
        this._applyAreas(updatedAreas);
      })
      .catch((error) => {
        this.$log.error('Failed to update clustering areas', error);
        this._showAreasUpdateError();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  // Hidden feature - create service area from location. Commented out until enabled.
  // onLocationChange({address}) {
  //   if (!address?.location || !!this.isDisabled) {
  //     return;
  //   }
  //
  //   if (this.isLocalOnly) {
  //     this.isLoading = true;
  //     this.serviceAreaRequests.searchAreasByAddressGeoLocation(address.location.lat, address.location.lng)
  //       .then((areas) => {
  //         if (!areas?.length) {
  //           return;
  //         }
  //
  //         const serviceAreaIds = _.uniq(_.concat(this.selectedServiceAreaIds, _.map(areas, 'areaId')));
  //         this.selectedServiceAreaIds = serviceAreaIds;
  //         this._loadLocalServiceAreas(serviceAreaIds);
  //         this._emitLocalChange(serviceAreaIds);
  //       })
  //       .catch((error) => {
  //         this.$log.error('Failed to create clustering area from location', error);
  //       })
  //       .finally(() => {
  //         this.isLoading = false;
  //       });
  //     return;
  //   }
  //
  //   if (!this._canManageAreas()) {
  //     return;
  //   }
  //
  //   this.isLoading = true;
  //   this.serviceAreaRequests.searchAreasByAddressGeoLocation(address.location.lat, address.location.lng)
  //     .then((areas) => {
  //       if (!areas?.length) {
  //         return;
  //       }
  //
  //       const serviceAreaIds = _.map(areas, 'areaId');
  //       const addedServiceAreaIds = _.difference(serviceAreaIds, this.selectedServiceAreaIds);
  //
  //       if (!addedServiceAreaIds.length) {
  //         return;
  //       }
  //
  //       return this._addClusteringAreas(addedServiceAreaIds);
  //     })
  //     .then((response) => {
  //       if (response) {
  //         const addedAreas = this._normalizeAreasResponse(response);
  //         const updatedAreas = this._mergeAreas(_.clone(this.clusteringAreas) || [], addedAreas);
  //         this._applyAreas(updatedAreas);
  //       }
  //     })
  //     .catch((error) => {
  //       this.$log.error('Failed to create clustering area from location', error);
  //     })
  //     .finally(() => {
  //       this.isLoading = false;
  //     });
  // }

  onEditClusteringAreaClick(clusteringArea: ClusteringAreaModel, $event?) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    if (!!this.isDisabled || !!this.isLocalOnly || !clusteringArea?.id) {
      return;
    }

    this._resolveAreaForEdit(clusteringArea)
      .then((areaForEdit) => {
        return this.planningDialogService.editClusteringArea(areaForEdit, $event);
      })
      .then((editedArea: ClusteringAreaModel) => {
        if (editedArea) {
          this._saveEditedClusteringArea(editedArea);
        }
      })
      .catch(() => {
        this.$log.info('Dialog Closed');
      });
  }

  private _resolveAreaForEdit(clusteringArea: ClusteringAreaModel) {
    // The clustering area carries its own polygon only once it has been customized.
    // Otherwise the geometry lives on the underlying service area, so fetch the full
    // clustering area first and fall back to the service area polygon when empty.
    if (clusteringArea.coordinates?.length) {
      return this.$q.when(clusteringArea);
    }

    this.isLoading = true;

    const fetchClusteringArea = this.ownerId
      ? this.planningClusteringAreasRequestsService.getClusteringAreasForMap(this.ownerId, [clusteringArea.id])
        .then((areas) => {
          const fullArea = _.find(areas, {id: clusteringArea.id});
          return fullArea ? {...clusteringArea, ...fullArea} : clusteringArea;
        })
        .catch((error) => {
          this.$log.error('Failed to load clustering area for edit', error);
          return clusteringArea;
        })
      : this.$q.when(clusteringArea);

    return fetchClusteringArea
      .then((area) => {
        return area.coordinates?.length ? area : this._loadServiceAreaPolygon(area);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _loadServiceAreaPolygon(clusteringArea: ClusteringAreaModel) {
    const serviceAreaId = this._getClusteringAreaServiceAreaId(clusteringArea);
    if (!serviceAreaId) {
      return this.$q.when(clusteringArea);
    }

    return this.$serviceArea().$asyncValues([serviceAreaId])
      .then((serviceAreas) => {
        const serviceArea = _.find(serviceAreas, (area) => area.areaId === serviceAreaId) || _.head(serviceAreas);
        if (serviceArea?.coordinates?.length) {
          return {
            ...clusteringArea,
            coordinates: serviceArea.coordinates,
            color: clusteringArea.color || serviceArea.color,
            name: clusteringArea.name || serviceArea.areaName || serviceArea.name
          };
        }
        return clusteringArea;
      })
      .catch((error) => {
        this.$log.error('Failed to load service area polygon for clustering area edit', error);
        return clusteringArea;
      });
  }

  onRemoveClusteringAreaClick(clusteringArea: ClusteringAreaModel, $event?) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.onRemoveClusteringArea(clusteringArea);
  }

  onRemoveClusteringArea(clusteringArea: ClusteringAreaModel) {
    if (!!this.isDisabled || !clusteringArea) {
      return;
    }

    if (this.isLocalOnly) {
      const serviceAreaIds = _.filter(this.selectedServiceAreaIds, (serviceAreaId) => {
        return serviceAreaId !== this._getClusteringAreaServiceAreaId(clusteringArea);
      });
      this.selectedServiceAreaIds = serviceAreaIds;
      this._loadLocalServiceAreas(serviceAreaIds);
      this._emitLocalChange(serviceAreaIds);
      return;
    }

    if (!clusteringArea?.id || !this._canManageAreas()) {
      return;
    }

    this.isLoading = true;
    this._removeClusteringArea(clusteringArea.id)
      .then(() => {
        const updatedAreas = _.reject(_.clone(this.clusteringAreas) || [], {id: clusteringArea.id});
        this._applyAreas(updatedAreas);
      })
      .catch((error) => {
        this.$log.error('Failed to remove clustering area', error);
        this._showAreasUpdateError();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _resolveOwnerType() {
    if (this.templateId) {
      this.ownerType = ClusteringOwnerType.TEMPLATE;
      this.ownerId = this.templateId;
    } else if (this.draftId && this.projectId) {
      this.ownerType = ClusteringOwnerType.DRAFT;
      if (!this.ownerId) {
        this.ownerId = this.draftId;
      }
    } else if (this.projectId) {
      this.ownerType = ClusteringOwnerType.PROJECT;
      this.ownerId = this.projectId;
    }
  }

  private _canManageAreas() {
    return !!this.ownerType && (
      this.ownerType === ClusteringOwnerType.TEMPLATE ? !!this.templateId :
        this.ownerType === ClusteringOwnerType.PROJECT ? !!this.projectId :
          !!(this.projectId && this.draftId)
    );
  }

  private _addClusteringAreas(areaIds: string[]) {
    if (this.ownerType === ClusteringOwnerType.TEMPLATE) {
      return this.planningClusteringAreasRequestsService.addClusteringAreasToTemplate(this.templateId, areaIds);
    }
    if (this.ownerType === ClusteringOwnerType.PROJECT) {
      return this.planningClusteringAreasRequestsService.addClusteringAreasToProject(this.projectId, areaIds);
    }
    return this.planningClusteringAreasRequestsService.addClusteringAreasToDraft(this.projectId, this.draftId, areaIds);
  }

  private _removeClusteringArea(areaId: string) {
    if (this.ownerType === ClusteringOwnerType.TEMPLATE) {
      return this.planningClusteringAreasRequestsService.removeClusteringAreaFromTemplate(this.templateId, areaId);
    }
    if (this.ownerType === ClusteringOwnerType.PROJECT) {
      return this.planningClusteringAreasRequestsService.removeClusteringAreaFromProject(this.projectId, areaId);
    }
    return this.planningClusteringAreasRequestsService.removeClusteringAreaFromDraft(this.projectId, this.draftId, areaId);
  }

  private _saveEditedClusteringArea(editedArea: ClusteringAreaModel) {
    this.isLoading = true;
    this.planningClusteringAreasRequestsService.editClusteringArea(editedArea.id, editedArea)
      .then((response) => {
        const updatedArea = this.planningClusteringAreasRequestsService.normalizeClusteringAreaFromEdit(response, editedArea);
        this.clusteringAreas = _.map(this.clusteringAreas, (area) => {
          return area.id === updatedArea.id ? updatedArea : area;
        });
        this._emitAreasUpdated();
        this._showAreasUpdateSuccess();
      })
      .catch((error) => {
        this.$log.error('Failed to edit clustering area', error);
        this._showAreasUpdateError();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _loadClusteringAreas() {
    if (!this.ownerId) {
      this.clusteringAreas = [];
      this.selectedServiceAreaIds = [];
      return;
    }

    if (!this.clusteringAreaIds?.length) {
      this.clusteringAreas = [];
      this.selectedServiceAreaIds = [];
      return;
    }

    this.isLoading = true;
    this.planningClusteringAreasRequestsService.getClusteringAreasByIds(this.ownerId, this.clusteringAreaIds)
      .then((response) => {
        this._setClusteringAreas(this._normalizeAreasResponse(response));
      })
      .catch((error) => {
        this.$log.error('Failed to load clustering areas', error);
        this.clusteringAreas = [];
        this.selectedServiceAreaIds = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _setClusteringAreas(areas: ClusteringAreaModel[]) {
    this.clusteringAreas = areas || [];
    this.selectedServiceAreaIds = _.uniq(_.compact(_.map(this.clusteringAreas, (area) => {
      return this._getClusteringAreaServiceAreaId(area);
    })));
  }

  private _mergeAreas(existingAreas: ClusteringAreaModel[], addedAreas: ClusteringAreaModel[]): ClusteringAreaModel[] {
    return _.uniqBy(
      _.unionBy(
        _.map(existingAreas || [], (area) => this.planningClusteringAreasRequestsService.normalizeClusteringArea(area)),
        _.map(addedAreas || [], (area) => this.planningClusteringAreasRequestsService.normalizeClusteringArea(area)),
        'id'
      ),
      (area) => this._getClusteringAreaServiceAreaId(area)
    );
  }

  private _applyAreas(areas: ClusteringAreaModel[]) {
    this._setClusteringAreas(areas);
    this._emitChange(_.map(this.clusteringAreas, 'id'));
  }

  private _hasSameAreas(ids: string[]): boolean {
    return _.isEmpty(_.xor(_.map(this.clusteringAreas, 'id'), ids || []));
  }

  private _normalizeAreasResponse(response): ClusteringAreaModel[] {
    return this.planningClusteringAreasRequestsService.normalizeClusteringAreasResponse(response);
  }

  private _showAreasUpdateError() {
    if (this.ownerType === ClusteringOwnerType.TEMPLATE) {
      this.planningAlertsService.updateTemplateError();
    } else if (this.ownerType === ClusteringOwnerType.DRAFT) {
      this.planningAlertsService.updateDraftSettingsError();
    } else {
      this.planningAlertsService.updateProjectSettingsError();
    }
  }

  private _showAreasUpdateSuccess() {
    if (this.ownerType === ClusteringOwnerType.TEMPLATE) {
      this.planningAlertsService.updateTemplateSuccess();
    } else if (this.ownerType === ClusteringOwnerType.DRAFT) {
      this.planningAlertsService.updateDraftSettingsSuccess();
    } else {
      this.planningAlertsService.updateProjectSettingsSuccess();
    }
  }

  private _getClusteringAreaServiceAreaId(area: ClusteringAreaModel): string {
    return area?.serviceAreaId || area?.areaReference?.id;
  }

  private _emitChange(clusteringAreaIds: string[]) {
    if (_.isFunction(this.onChange)) {
      this.onChange({clusteringAreaIds: clusteringAreaIds || []});
    }
  }

  private _emitAreasUpdated() {
    if (_.isFunction(this.onAreasUpdated)) {
      this.onAreasUpdated({clusteringAreas: angular.copy(this.clusteringAreas)});
    }
  }

  private _emitLocalChange(serviceAreaIds: string[]) {
    if (_.isFunction(this.onChange)) {
      this.onChange({serviceAreaIds: serviceAreaIds || []});
    }
  }

  private _loadLocalServiceAreas(serviceAreaIds: string[]) {
    this.selectedServiceAreaIds = serviceAreaIds || [];

    if (!this.selectedServiceAreaIds.length) {
      this.clusteringAreas = [];
      this.serviceAreaColorMap = {};
      return;
    }

    this.isLoading = true;
    this.$serviceArea().$asyncValues(this.selectedServiceAreaIds)
      .then((serviceAreas) => {
        this.serviceAreaColorMap = _.keyBy(serviceAreas, 'areaId');
        this.clusteringAreas = _.map(this.selectedServiceAreaIds, (serviceAreaId) => {
          const serviceArea = _.get(this.serviceAreaColorMap, serviceAreaId);
          return this.planningClusteringAreasRequestsService.normalizeClusteringArea({
            id: serviceAreaId,
            serviceAreaId: serviceAreaId,
            name: serviceArea?.areaName,
            serviceAreaName: serviceArea?.areaName,
            color: serviceArea?.color || '#00adee'
          });
        });
      })
      .catch((error) => {
        this.$log.error('Failed to load service areas', error);
        this.clusteringAreas = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

}

angular
  .module('bringoz.admin.planning')
  .component('planningClusteringAreasSettings', {
    bindings: {
      ownerId: '<?',
      templateId: '<?',
      projectId: '<?',
      draftId: '<?',
      clusteringAreaIds: '<?',
      serviceAreaIds: '<?',
      isLocalOnly: '<?',
      isDisabled: '<?',
      onChange: '&',
      onAreasUpdated: '&?'
    },
    template: require('./planning-clustering-areas-settings.html').default,
    controller: PlanningClusteringAreasSettingsCtrl
  });
